import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { formService } from '../../services/formService';
import type { Form, FormTheme } from '../../types/form';
import { resolveFormTheme } from '../../types/form';
import { FormPreviewForm } from './FormPreviewForm';
import {
  buildFormThemeCssVars,
  ensureFormThemeFontsLoaded,
} from './FormThemePreview';

type PageState = 'loading' | 'ready' | 'notFound';

function PreviewTopBar({ formId }: { formId: string }) {
  return (
    <header className="border-b border-gray-200 bg-white shadow-sm">
      <div className="mx-auto flex h-14 max-w-3xl items-center justify-between gap-3 px-4">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to={`/forms/${formId}`}
            className="shrink-0 text-gray-500 hover:text-gray-800"
            title="Voltar ao editor"
            aria-label="Voltar ao editor"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </Link>
          <span className="truncate text-sm font-medium text-gray-800">
            Modo de visualização
          </span>
        </div>
        <Link
          to={`/forms/${formId}`}
          className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Voltar ao editor
        </Link>
      </div>
    </header>
  );
}

function PreviewNotAcceptingBanner({ formId }: { formId: string }) {
  return (
    <div className="border-b border-amber-200 bg-amber-50">
      <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 text-sm text-amber-900">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>
            Esta é uma visualização. O formulário não está aceitando respostas
            nesta tela.
          </span>
        </div>
        <Link
          to={`/forms/${formId}`}
          className="shrink-0 text-sm font-medium text-amber-900 underline hover:text-amber-950"
        >
          Gerenciar configurações de publicação
        </Link>
      </div>
    </div>
  );
}

function PreviewShell({
  theme,
  formId,
  children,
}: {
  theme: FormTheme;
  formId: string;
  children: React.ReactNode;
}) {
  const resolved = resolveFormTheme(theme);

  useEffect(() => {
    ensureFormThemeFontsLoaded();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" style={buildFormThemeCssVars(resolved)}>
      <PreviewTopBar formId={formId} />
      <PreviewNotAcceptingBanner formId={formId} />
      <div className="px-4 py-8">{children}</div>
    </div>
  );
}

export const FormPreviewPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [form, setForm] = useState<Form | null>(null);

  const theme = useMemo(
    () => resolveFormTheme(form?.theme),
    [form?.theme]
  );

  const loadForm = useCallback(async () => {
    if (!formId)
    {
      setPageState('notFound');
      return;
    }

    try
    {
      setPageState('loading');
      const data = await formService.getById(formId);
      setForm(data);
      setPageState('ready');
    }
    catch
    {
      setPageState('notFound');
    }
  }, [formId]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  if (!formId)
  {
    return null;
  }

  if (pageState === 'loading')
  {
    return (
      <PreviewShell theme={theme} formId={formId}>
        <div className="flex justify-center py-20 text-gray-500">
          Carregando visualização...
        </div>
      </PreviewShell>
    );
  }

  if (pageState === 'notFound' || !form)
  {
    return (
      <PreviewShell theme={theme} formId={formId}>
        <div className="mx-auto w-full max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900">
            Formulário não encontrado
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Não foi possível carregar a visualização deste formulário.
          </p>
          <div className="mt-6 flex justify-center">
            <Link to={`/forms/${formId}`}>
              <Button type="button" variant="primary">
                Voltar ao editor
              </Button>
            </Link>
          </div>
        </div>
      </PreviewShell>
    );
  }

  return (
    <PreviewShell theme={theme} formId={formId}>
      <FormPreviewForm form={form} />
    </PreviewShell>
  );
};

export default FormPreviewPage;
